import { ProgressBar, SearchBarProps } from '@zeiss/beyond-online-react';
import { EmptyState } from '@zeiss/lib-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RequestCreationButton, ResultItems } from './';
import { ActionBar } from 'src/components/shared/ActionBar';
import { CreateButton } from 'src/components/shared/CreateButton';
import {
  GLOBAL_ACTIONS,
  GLOBAL_RESOURCES,
  MIN_CHARS_FOR_SEARCH,
} from 'src/constants';
import { searchItems } from 'src/helpers/utils';
import { useGlobalEvaluations } from 'src/hooks';
import { useApplicationLists } from 'src/hooks/api';
import { ApplicationListResponse } from 'src/types/rest/tenant';

// TODO: Migrate to BE search
export const Result = () => {
  const { t } = useTranslation(['common']);
  const [searchParam, setSearchParam] = useState<string>('');

  const { can } = useGlobalEvaluations();
  const {
    data: applicationListsData,
    error,
    isPending,
  } = useApplicationLists();

  const canCreateApplicationList = can({
    action: GLOBAL_ACTIONS.TM_APPLICATION_LIST_CREATE,
    resource: GLOBAL_RESOURCES.TM_APPLICATION_LIST,
  });

  const primaryCtaButton = canCreateApplicationList ? (
    <CreateButton transX={t('common:glossary.applicationList')} />
  ) : (
    <RequestCreationButton />
  );

  const handleSearch: SearchBarProps['onSearch'] = (_, { searchValue }) => {
    setSearchParam(searchValue);
  };

  const renderResult = () => {
    if (error) {
      return <div>Error loading data</div>;
    }

    if (isPending) {
      return <ProgressBar animate="spinning" data-testid="LoadingSpinner" />;
    }

    if (applicationListsData.totalItems > 0) {
      const searchParamsKeys: (keyof ApplicationListResponse)[] = ['name'];

      let filteredItems = applicationListsData.items;
      if (searchParam && searchParam.length >= MIN_CHARS_FOR_SEARCH) {
        filteredItems = searchItems({
          items: applicationListsData.items,
          params: searchParamsKeys,
          query: searchParam,
        });
      }

      if (filteredItems.length) {
        return <ResultItems items={filteredItems} />;
      }
    }

    if (applicationListsData.totalItems === 0 && !searchParam) {
      return (
        <EmptyState
          description={t('common:emptyState.applicationLists')}
          headline={t('common:emptyState.textNoX', {
            x: t('common:glossary.applicationList_other'),
          })}
          illustrationConfig={{
            img: 'ClearedData',
          }}
        />
      );
    }

    return (
      <EmptyState
        description={t('common:emptyState.tryDifferentTerm')}
        headline={t('common:emptyState.noXfound', {
          x: t('common:glossary.applicationList'),
        })}
        illustrationConfig={{
          img: 'SearchNotFound',
        }}
      />
    );
  };

  return (
    <>
      <ActionBar onSearch={handleSearch}>{primaryCtaButton}</ActionBar>

      {renderResult()}
    </>
  );
};
